import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLogSession } from "../../contexts/LogSessionContext";
import { useLang } from "../../contexts/LangContext";
import { getLocalDateString } from "../../utils/misc";
import ModalWindow from "../../components/ModalWindow";
import MealLog from "../../cards/MealLog";

export default function AnyFishToday() {
  const navigate = useNavigate();
  const { t } = useLang();
  const { person, setcurrentDayRecord, setcurrentMealRecord, setNewFishItem } =
    useLogSession();
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);

  useEffect(() => {
    const localTime = new Date();
    if (localTime.getHours() < 8) {
      // If it's before 8 AM, navigate to the "any fish yesterday" page
      navigate("../any-fish-yesterday", { replace: true });
    } else {
      setcurrentDayRecord(null); // Reset any previous day record
      setcurrentMealRecord(null);
      setNewFishItem(null);
    }
  }, []);

  // console.log(person);

  const today = getLocalDateString(new Date());

  const hasPreviousRecord = person.records.some((record) => {
    return record.date === today;
  });

  const consumedTodayAlready = person.records.some((record) => {
    return (
      record.date === today &&
      record.meals &&
      record.meals.length > 0 &&
      record.meals.some((meal) => meal.fish_items && meal.fish_items.length > 0)
    );
  });

  const handleYes = () => {
    const existingRecord = person.records.find(
      (record) => record.date === today
    );

    setcurrentDayRecord(
      existingRecord || {
        date: today,
        meals: [],
        surveyed_on: new Date().toISOString(),
      }
    );

    console.log("Current day record set for today:", today);
    navigate("../which-meal", { relative: "path" }); // Navigate to the next step in the survey
  };

  const handleNo = () => {
    navigate("../any-fish-yesterday", { relative: "path" });
  };

  function showTodaysLog(): void {
    let existingRecord = person.records.find(
      (record) =>
        record.date === today && record.meals && record.meals.length > 0
    );
    if (existingRecord) {
      // Sort by meal type to ensure consistent order
      const mealOrder = ["breakfast", "lunch", "snack", "dinner"];
      existingRecord.meals = existingRecord.meals.sort((a, b) => {
        return mealOrder.indexOf(a.meal_type) - mealOrder.indexOf(b.meal_type);
      });
      setModalContent(
        <div className="bg-white bg-opacity-75 backdrop-blur-md p-4 rounded-xl shadow-lg max-w-xl max-h-[80dvh] overflow-scroll mx-scr">
          <div className="flex flex-col gap-4 bg-white rounded-lg p-4">
            {existingRecord.meals.length > 0 &&
              existingRecord.meals.map((meal, index) => (
                <MealLog
                  key={index}
                  meal={meal}
                  allowEdit={false}
                  allowDelete={false}
                />
              ))}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col grow items-center justify-between gap-10 mt-12 text-center">
      <div className="flex flex-col items-center justify-center w-full  px-scr">
        <h1 className="text-3xl font-bold text-rose-700">
          {consumedTodayAlready
            ? t("anyOtherFishToday_Heading")
            : t("anyFishToday_Heading")}
        </h1>

        <div className="flex flex-col gap-4 w-full max-w-xs mt-16 mb-8">
          <button
            type="button"
            onClick={handleYes}
            className="w-full button-base button-bg-grad-green"
          >
            {t("anyFishToday_Yes")}
          </button>

          <button
            type="button"
            onClick={handleNo}
            className="w-full button-base button-bg-grad-2"
          >
            {t("anyFishToday_No")}
          </button>
        </div>
      </div>
      
      <div className="bottom-nav-bar flex justify-center gap-2 w-full sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
        {consumedTodayAlready && (
          <button
            type="button"
            onClick={() => showTodaysLog()}
            className="button-xs button-bg-grad-lightgray !text-gray-600"
          >
            {t("anyFishToday_ViewTodaysLog")}
          </button>
        )}
      </div>

      {modalContent && (
        <ModalWindow
          isOpen={!!modalContent}
          onClose={() => setModalContent(null)}
          modalTitle={t("anyFishToday_ViewTodaysLogModalTitle")}
        >
          {modalContent}
        </ModalWindow>
      )}
    </div>
  );
}
